import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PageHeader from '../../../components/pageHeader';
import DataLoading from '../../../components/dataLoading';
import { getAllKYC, assignKYC, getAssociateProfile } from './kycAdminService';
import KYCReviewList from './components/KYCReviewList';
import KYCReviewModalSteps from './components/KYCReviewModalSteps';
import './KYCAdminReview.scss';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Normalise status strings from the API (may be camelCase or spaced)
const normaliseStatus = (s = '') => {
  if (!s) return '';
  const stripped = s.toLowerCase().replace(/[\s_-]/g, '');
  const map = {
    notstarted: 'STARTED',
    started: 'STARTED',
    draft: 'STARTED',
    new: 'STARTED',
    submitted: 'SUBMITTED',
    pending: 'SUBMITTED',
    underreview: 'UNDER REVIEW',
    inreview: 'UNDER REVIEW',
    reviewing: 'UNDER REVIEW',
    assigned: 'UNDER REVIEW',
    needsmoreinfo: 'NEEDS MORE INFO',
    needsinfo: 'NEEDS MORE INFO',
    moreinforequired: 'NEEDS MORE INFO',
    changesrequested: 'NEEDS MORE INFO',
    pendingchanges: 'NEEDS MORE INFO',
    inforequested: 'NEEDS MORE INFO',
    sentback: 'NEEDS MORE INFO',
    approved: 'APPROVED',
    verified: 'APPROVED',
    accepted: 'APPROVED',
    rejected: 'REJECTED',
    denied: 'REJECTED',
  };
  return map[stripped] || map[s.toLowerCase().trim()] || s.toUpperCase().trim();
};

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'STARTED', label: 'Not Started' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'UNDER REVIEW', label: 'Under Review' },
  { key: 'NEEDS MORE INFO', label: 'Needs More Info' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
];

const KYCAdminReview = () => {
  const { t } = useTranslation();

  // Current reviewer identity (used for Assigned to Me tab)
  // login.js stores email in associateData, not apiToken
  const currentReviewer = useMemo(() => {
    try {
      const assocData = JSON.parse(localStorage.getItem('associateData') || '{}');
      if (assocData.email) return assocData.email.toLowerCase();
      // Fallback: try apiToken fields
      const token = JSON.parse(localStorage.getItem('apiToken') || '{}');
      return (token.emailAddress || token.email || token.username || '').toLowerCase();
    } catch {
      return '';
    }
  }, []);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allReviews, setAllReviews] = useState([]);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedToMe, setAssignedToMe] = useState(
    () => localStorage.getItem('kyc_admin_assigned') === 'true'
  );

  const handleAssignedToggle = () => {
    setAssignedToMe((prev) => {
      localStorage.setItem('kyc_admin_assigned', String(!prev));
      return !prev;
    });
  };

  // Persist last selected tab in localStorage
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('kyc_admin_tab') || ''
  );

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    localStorage.setItem('kyc_admin_tab', tabKey);
  };

  const [filters, setFilters] = useState({
    // No status sent to API — we filter client-side to avoid casing issues
    countryCode: '',
    type: '',
    reviewerName: '',
    sortBy: '',
    sortOrder: '',
    page: 1,
    pageSize: 20,
  });

  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Always fetch all records — tab/status filtering done client-side
      const response = await getAllKYC({ ...filters, status: '', pageSize: 500 });

      const raw = Array.isArray(response)
        ? response
        : response?.data || response?.items || [];

      // Filter out wrapper/error objects that sneaked in (no real kycId)
      const validList = (Array.isArray(raw) ? raw : []).filter(
        (item) => item && item.associateId != null && item.kycId != null
      );

      // Log raw statuses from API for diagnostic purposes
      const rawStatuses = [...new Set(validList.map((k) => k.status))];
      console.log('[KYCAdminReview] Raw API statuses:', rawStatuses);
      console.log('[KYCAdminReview] Records by raw status:', rawStatuses.map((s) => ({
        raw: s,
        normalised: normaliseStatus(s),
        count: validList.filter((k) => k.status === s).length,
      })));

      // Normalise status field so tabs work regardless of API casing
      const normList = validList.map((kyc) => ({
        ...kyc,
        _normStatus: normaliseStatus(kyc.status),
      }));

      setAllReviews(normList);

      // Batch-fetch associate names in background
      if (normList.length > 0) {
        Promise.allSettled(normList.map((k) => getAssociateProfile(k.associateId))).then(
          (results) => {
            setAllReviews(
              normList.map((kyc, i) => {
                const profile =
                  results[i]?.status === 'fulfilled' ? results[i].value : null;
                return {
                  ...kyc,
                  firstName: profile?.firstName || '',
                  lastName: profile?.lastName || '',
                };
              })
            );
          }
        );
      }
    } catch (err) {
      console.error('[KYCAdminReview] Error fetching reviews:', err);
      setError('Failed to load KYC reviews');
      toast.error('Failed to load KYC reviews');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // KPI counts derived from already-loaded data
  const kpiCounts = useMemo(() => {
    const now = new Date();
    return {
      submitted: allReviews.filter((r) => r._normStatus === 'SUBMITTED').length,
      underReview: allReviews.filter((r) => r._normStatus === 'UNDER REVIEW').length,
      needsInfo: allReviews.filter((r) => r._normStatus === 'NEEDS MORE INFO').length,
      approvedMonth: allReviews.filter((r) => {
        if (r._normStatus !== 'APPROVED') return false;
        const d = new Date(r.approvedDate || r.apporvedDate || 0);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length,
    };
  }, [allReviews]);

  // Client-side tab + search + assigned-to-me filtering
  const filteredReviews = useMemo(() => {
    let list = allReviews;

    // Assigned to Me toggle (stacks with status tab)
    if (assignedToMe && currentReviewer) {
      list = list.filter((kyc) => {
        const rev = (kyc.reviewer || kyc.reviewerName || '').toLowerCase();
        return rev.includes(currentReviewer);
      });
    }

    // Status tab filter
    if (activeTab) {
      list = list.filter((kyc) => kyc._normStatus === activeTab);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((kyc) => {
        const fullName = `${kyc.firstName || ''} ${kyc.lastName || ''}`.toLowerCase();
        const id = String(kyc.associateId || '');
        return fullName.includes(q) || id.includes(q);
      });
    }

    return list;
  }, [allReviews, activeTab, assignedToMe, searchQuery, currentReviewer]);

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setFilters((prev) => ({ ...prev, pageSize: parseInt(newSize), page: 1 }));
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Search is client-side; just prevent default
  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  const handleAssignKYC = async (associateId) => {
    setIsAssigning(associateId);
    try {
      await assignKYC(associateId);
      toast.success('KYC assigned to you');
      fetchReviews();
    } catch (err) {
      console.error('Error assigning KYC:', err);
      toast.error(err.response?.data?.message || 'Failed to assign KYC');
    } finally {
      setIsAssigning(null);
    }
  };

  const handleReviewClick = (kyc) => {
    setSelectedKYC(kyc);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedKYC(null);
  };

  const handleActionComplete = () => {
    setShowModal(false);
    setSelectedKYC(null);
    fetchReviews();
  };

  const hasActiveFilters = !!searchQuery || assignedToMe;

  const displayCount = filteredReviews.length;
  const totalPages = Math.ceil(displayCount / filters.pageSize) || 1;

  if (isLoading && allReviews.length === 0) {
    return (
      <PageHeader title={t('kyc.admin.title')} preTitle={t('kyc.admin.preTitle')}>
        <DataLoading />
      </PageHeader>
    );
  }

  if (error && allReviews.length === 0) {
    return (
      <PageHeader title={t('kyc.admin.title')} preTitle={t('kyc.admin.preTitle')}>
        <div className='container-xl'>
          <div className='card'>
            <div className='card-body text-center py-5'>
              <div className='text-danger mb-3'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='48'
                  height='48'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <circle cx='12' cy='12' r='10'></circle>
                  <line x1='12' y1='8' x2='12' y2='12'></line>
                  <line x1='12' y1='16' x2='12.01' y2='16'></line>
                </svg>
              </div>
              <h3>{error}</h3>
              <button className='btn btn-primary mt-3' onClick={() => fetchReviews()}>
                {t('retry')}
              </button>
            </div>
          </div>
        </div>
      </PageHeader>
    );
  }

  return (
    <PageHeader title={t('kyc.admin.title')} preTitle={t('kyc.admin.preTitle')}>
      <div className='container-xl kyc-admin-review'>
        {/* KPI Summary Row */}
        <div className='row row-deck row-cards mb-3'>
          <div className='col-sm-6 col-lg-3'>
            <div className='card'>
              <div className='card-body'>
                <div className='d-flex align-items-center'>
                  <div className='subheader'>Awaiting Review</div>
                </div>
                <div className='h1 mb-0'>{kpiCounts.submitted}</div>
                <div className='d-flex mb-2'>
                  <span className='badge bg-blue-lt'>Submitted</span>
                </div>
              </div>
            </div>
          </div>
          <div className='col-sm-6 col-lg-3'>
            <div className='card'>
              <div className='card-body'>
                <div className='subheader'>In Progress</div>
                <div className='h1 mb-0'>{kpiCounts.underReview}</div>
                <div className='d-flex mb-2'>
                  <span className='badge bg-yellow-lt'>Under Review</span>
                </div>
              </div>
            </div>
          </div>
          <div className='col-sm-6 col-lg-3'>
            <div className='card'>
              <div className='card-body'>
                <div className='subheader'>Needs Correction</div>
                <div className='h1 mb-0'>{kpiCounts.needsInfo}</div>
                <div className='d-flex mb-2'>
                  <span className='badge bg-orange-lt'>Needs More Info</span>
                </div>
              </div>
            </div>
          </div>
          <div className='col-sm-6 col-lg-3'>
            <div className='card'>
              <div className='card-body'>
                <div className='subheader'>Approved This Month</div>
                <div className='h1 mb-0'>{kpiCounts.approvedMonth}</div>
                <div className='d-flex mb-2'>
                  <span className='badge bg-green-lt'>Approved</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Tabs + Assigned to Me toggle */}
        <div
          className='card mb-0'
          style={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderBottom: 'none',
          }}
        >
          <div
            className='card-header d-flex align-items-center justify-content-between'
            style={{ borderBottom: 'none' }}
          >
            <ul className='nav nav-tabs card-header-tabs'>
              {STATUS_TABS.map((tab) => (
                <li key={tab.key} className='nav-item'>
                  <button
                    className={`nav-link${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Assigned to Me toggle — right side of the tab bar */}
            <div className='form-check form-switch mb-0 ms-3 d-flex align-items-center gap-2'>
              <input
                className='form-check-input'
                type='checkbox'
                role='switch'
                id='assignedToMeSwitch'
                checked={assignedToMe}
                onChange={handleAssignedToggle}
                style={{ cursor: 'pointer' }}
              />
              <label
                className='form-check-label text-nowrap'
                htmlFor='assignedToMeSwitch'
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Assigned to me
              </label>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div
          className='card mb-0'
          style={{ borderRadius: 0, borderTop: 'none', borderBottom: 'none' }}
        >
          <div className='card-body py-2'>
            <form
              onSubmit={handleSearchSubmit}
              className='d-flex gap-2 align-items-center'
            >
              <div className='input-group flex-1'>
                <span className='input-group-text'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <circle cx='11' cy='11' r='8'></circle>
                    <line x1='21' y1='21' x2='16.65' y2='16.65'></line>
                  </svg>
                </span>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Search by name or ID…'
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                {searchQuery && (
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => {
                      setSearchQuery('');
                      fetchReviews({ ...filters, search: '', page: 1 });
                    }}
                  >
                    ✕
                  </button>
                )}
                <button type='submit' className='btn btn-primary'>
                  Search
                </button>
              </div>

              <button
                type='button'
                className='btn btn-outline-secondary'
                onClick={() => fetchReviews()}
                disabled={isLoading}
                title='Refresh'
              >
                {isLoading ? (
                  <span className='spinner-border spinner-border-sm' role='status'></span>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <polyline points='23 4 23 10 17 10'></polyline>
                    <polyline points='1 20 1 14 7 14'></polyline>
                    <path d='M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15'></path>
                  </svg>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Results Card */}
        <div className='card' style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
          <div className='card-header'>
            <h3 className='card-title'>
              KYC Submissions
              {displayCount > 0 && (
                <span className='badge bg-blue-lt ms-2'>{displayCount}</span>
              )}
            </h3>
          </div>

          <div className='card-body p-0'>
            {filteredReviews.length === 0 ? (
              <div className='text-center py-5'>
                <div className='text-muted mb-3'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='64'
                    height='64'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1'
                  >
                    <path d='M9 11l3 3L22 4'></path>
                    <path d='M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11'></path>
                  </svg>
                </div>
                <h3>
                  {hasActiveFilters
                    ? 'No results match your search or filters'
                    : t('kyc.admin.noReviews')}
                </h3>
                <p className='text-muted'>{t('kyc.admin.noReviewsDescription')}</p>
              </div>
            ) : (
              <KYCReviewList
                reviews={filteredReviews}
                onReviewClick={handleReviewClick}
                onAssignClick={handleAssignKYC}
                isAssigning={isAssigning}
              />
            )}
          </div>

          {/* Pagination Footer */}
          {displayCount > 0 && (
            <div className='card-footer d-flex align-items-center justify-content-between'>
              <div className='d-flex align-items-center gap-2'>
                <span className='text-muted small'>Show</span>
                <select
                  className='form-select form-select-sm'
                  style={{ width: '80px' }}
                  value={filters.pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className='text-muted small'>per page</span>
              </div>
              <div className='d-flex align-items-center gap-2'>
                <span className='text-muted small'>
                  Page {filters.page} of {totalPages} · {displayCount} total
                </span>
                <div className='btn-group'>
                  <button
                    className='btn btn-sm btn-outline-secondary'
                    onClick={() => handlePageChange(filters.page - 1)}
                    disabled={filters.page <= 1 || isLoading}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className='btn btn-sm btn-outline-secondary'
                    onClick={() => handlePageChange(filters.page + 1)}
                    disabled={filters.page >= totalPages || isLoading}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showModal && selectedKYC && (
        <KYCReviewModalSteps
          kyc={selectedKYC}
          onClose={handleModalClose}
          onActionComplete={handleActionComplete}
        />
      )}
    </PageHeader>
  );
};

export default KYCAdminReview;
